/**
 * ReportBuilder — 5-step custom report wizard.
 * Accessed from Executive Dashboard → "Build a Custom Report" button.
 * Steps: 1=Domain, 2=Columns, 3=Filters, 4=Group+Sort, 5=Preview+Export
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Save, RotateCcw, Check, X, Plus, Loader2 } from 'lucide-react';
import { apiGet, apiPost, apiDel, api } from '../../lib/api';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';

const OPERATORS = [
  { value: 'eq',          label: '=' },
  { value: 'ne',          label: '≠' },
  { value: 'gt',          label: '>' },
  { value: 'lt',          label: '<' },
  { value: 'gte',         label: '≥' },
  { value: 'lte',         label: '≤' },
  { value: 'contains',    label: 'contains' },
  { value: 'in',          label: 'in (comma list)' },
  { value: 'date_between','label': 'date between (val1,val2)' },
];
const AGG_OPTIONS = [
  { value: 'count', label: 'Count rows' },
  { value: 'sum',   label: 'Sum' },
  { value: 'avg',   label: 'Average' },
];

const STEP_LABELS = ['Domain', 'Columns', 'Filters', 'Group & Sort', 'Preview'];

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEP_LABELS.map((label, i) => {
        const s = i + 1;
        const done = step > s;
        const active = step === s;
        return (
          <React.Fragment key={s}>
            <div className={`flex flex-col items-center gap-1 ${active ? 'opacity-100' : done ? 'opacity-70' : 'opacity-40'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors
                ${active ? 'bg-[#3b4872] text-white border-[#3b4872]' : done ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-400 border-slate-300'}`}>
                {done ? <Check size={14} /> : s}
              </div>
              <span className="text-[10px] font-display font-bold uppercase tracking-wider whitespace-nowrap">{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${done ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ReportBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [domains, setDomains] = useState({});
  const [domain, setDomain] = useState('');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templateName, setTemplateName] = useState('');

  useEffect(() => {
    apiGet('/reports/builder/domains').then(setDomains).catch(() => toast.error('Could not load domains'));
  }, []);

  // Load saved template if ?template=id in URL
  useEffect(() => {
    const tid = searchParams.get('template');
    if (!tid || !Object.keys(domains).length) return;
    apiGet('/reports/builder/templates').then(({ items }) => {
      const t = items.find(x => x.id === tid);
      if (!t) return;
      setTemplateName(t.name);
      setDomain(t.domain);
      setColumns(t.columns || []);
      setFilters(t.filters || []);
      setGroupBy(t.group_by || null);
      setSortBy(t.sort_by || '');
      setSortDir(t.sort_dir || 'desc');
      setStep(2);
      toast.info(`Loaded template: ${t.name}`);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, Object.keys(domains).length]);

  // Settings_Insights_Audit_Fix Item 3 — pre-select a domain from ?domain=X
  // (used by the Reports & MIS static catalogue cards to jump straight into
  // the matching Report Builder domain instead of a dead "coming soon" alert).
  useEffect(() => {
    const d = searchParams.get('domain');
    if (!d || searchParams.get('template') || !Object.keys(domains).length) return;
    if (!domains[d]) { toast.error(`Unknown report domain: ${d}`); return; }
    setDomain(d);
    setColumns([]);
    setStep(2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, Object.keys(domains).length]);

  const currentFields = domains[domain]?.fields || [];

  // Computed: does the current column selection include any numeric fields?
  const numericColumnsSelected = columns.some(
    c => currentFields.find(f => f.field === c)?.type === 'number'
  );

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await apiPost('/reports/builder/preview', {
        name: templateName || 'Untitled', domain, columns, filters,
        group_by: groupBy, sort_by: sortBy || null, sort_dir: sortDir,
      });
      setPreview(r);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const exportExcel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/reports/builder/export.xlsx',
        { name: templateName || 'Untitled', domain, columns, filters,
          group_by: groupBy, sort_by: sortBy || null, sort_dir: sortDir },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `${domain}_report.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally { setLoading(false); }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Give the template a name first'); return; }
    try {
      await apiPost('/reports/builder/templates',
        { name: templateName, domain, columns, filters,
          group_by: groupBy, sort_by: sortBy || null, sort_dir: sortDir });
      toast.success(`Template "${templateName}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const reset = () => {
    setStep(1); setDomain(''); setColumns([]); setFilters([]);
    setGroupBy(null); setSortBy(''); setSortDir('desc'); setPreview(null); setTemplateName('');
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="report-builder">
      <button onClick={() => navigate('/app')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Back to Dashboard
      </button>
      <PageHeader overline="Reports & Analytics" title="Custom Report Builder"
        subtitle="Build, preview and export any data slice across all modules. Results are automatically scoped to your access." />

      <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
        <StepBar step={step} />

        {/* ── Step 1: Domain ── */}
        {step === 1 && (
          <div data-testid="step-domain">
            <h3 className="font-display font-bold text-base mb-4">Choose a Data Domain</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(domains).map(([key, cfg]) => (
                <button key={key}
                  onClick={() => { setDomain(key); setColumns([]); setStep(2); }}
                  className="text-left px-4 py-3 border border-slate-200 rounded-sm hover:border-[#3b4872] hover:bg-indigo-50/30 transition-colors"
                  data-testid={`domain-${key}`}>
                  <div className="font-display font-bold text-sm text-slate-800">{cfg.label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{cfg.fields.length} fields</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Columns ── */}
        {step === 2 && (
          <div data-testid="step-columns">
            <h3 className="font-display font-bold text-base mb-1">Select Columns</h3>
            <p className="text-xs text-slate-500 mb-4">Domain: <strong>{domains[domain]?.label}</strong> — pick which fields to include in the report.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {currentFields.map(f => {
                const on = columns.includes(f.field);
                return (
                  <label key={f.field} className={`flex items-center gap-2.5 px-3 py-2 border rounded-sm cursor-pointer transition-colors ${on ? 'border-[#3b4872] bg-indigo-50/40' : 'border-slate-200 hover:border-[#3b4872]/40'}`}
                    data-testid={`col-${f.field}`}>
                    <input type="checkbox" className="accent-[#3b4872]" checked={on}
                      onChange={() => setColumns(prev => on ? prev.filter(c => c !== f.field) : [...prev, f.field])} />
                    <div>
                      <div className="text-sm font-medium text-slate-800">{f.label}</div>
                      <div className="text-[10px] text-slate-400">{f.type}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50 flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
              <button onClick={() => setStep(3)} disabled={columns.length === 0}
                className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-40 flex items-center gap-1">
                Next ({columns.length} columns) <ArrowRight size={12} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Filters ── */}
        {step === 3 && (
          <div data-testid="step-filters">
            <h3 className="font-display font-bold text-base mb-1">Add Filters <span className="text-slate-400 font-normal">(optional)</span></h3>
            <p className="text-xs text-slate-500 mb-4">Narrow down rows before grouping or exporting.</p>
            <div className="space-y-2 mb-4">
              {filters.map((f, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`filter-row-${idx}`}>
                  <select value={f.field} onChange={e => setFilters(prev => prev.map((x, i) => i === idx ? {...x, field: e.target.value} : x))}
                    className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs bg-white flex-1">
                    {currentFields.map(ff => <option key={ff.field} value={ff.field}>{ff.label}</option>)}
                  </select>
                  <select value={f.operator} onChange={e => setFilters(prev => prev.map((x, i) => i === idx ? {...x, operator: e.target.value} : x))}
                    className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs bg-white w-32">
                    {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input value={f.value} onChange={e => setFilters(prev => prev.map((x, i) => i === idx ? {...x, value: e.target.value} : x))}
                    placeholder="value" className="border border-slate-300 rounded-sm px-2 py-1.5 text-xs flex-1" />
                  <button onClick={() => setFilters(prev => prev.filter((_, i) => i !== idx))}
                    className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
                </div>
              ))}
              <button onClick={() => setFilters(prev => [...prev, { field: currentFields[0]?.field || '', operator: 'eq', value: '' }])}
                className="text-xs text-[#3b4872] underline flex items-center gap-1" data-testid="add-filter-btn">
                <Plus size={11} /> Add filter
              </button>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setStep(2)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50 flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
              <button onClick={() => setStep(4)} className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold flex items-center gap-1">
                Next <ArrowRight size={12} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Group & Sort ── */}
        {step === 4 && (
          <div data-testid="step-groupby">
            <h3 className="font-display font-bold text-base mb-1">Group & Sort <span className="text-slate-400 font-normal">(optional)</span></h3>
            <p className="text-xs text-slate-500 mb-4">Aggregate rows by a field, or just choose a sort order.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Group by field</label>
                <select value={groupBy?.field || ''} onChange={e => setGroupBy(e.target.value ? { field: e.target.value, aggregate: null } : null)}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="groupby-field">
                  <option value="">— No grouping —</option>
                  {columns.map(c => {
                    const fd = currentFields.find(f => f.field === c);
                    return <option key={c} value={c}>{fd?.label || c}</option>;
                  })}
                </select>
              </div>
              {groupBy?.field && (
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Aggregation</label>
                  <select value={groupBy.aggregate || ''} onChange={e => setGroupBy(g => ({...g, aggregate: e.target.value || null}))}
                    className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="groupby-agg">
                    <option value="">— Group only, no aggregate —</option>
                    <option value="count">Count rows</option>
                    <option value="sum" disabled={!numericColumnsSelected}>
                      Sum{!numericColumnsSelected ? ' (select a numeric column first)' : ''}
                    </option>
                    <option value="avg" disabled={!numericColumnsSelected}>
                      Average{!numericColumnsSelected ? ' (select a numeric column first)' : ''}
                    </option>
                  </select>
                  {!numericColumnsSelected && groupBy.aggregate && groupBy.aggregate !== 'count' && (
                    <p className="text-[11px] text-amber-600 mt-1">Go back to Step 2 and select a numeric column (e.g. Contract Value, Quantity) to use Sum or Average.</p>
                  )}
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Sort by</label>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="sortby-field">
                  <option value="">— Default (created_at) —</option>
                  {columns.map(c => {
                    const fd = currentFields.find(f => f.field === c);
                    return <option key={c} value={c}>{fd?.label || c}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1.5">Sort direction</label>
                <select value={sortDir} onChange={e => setSortDir(e.target.value)}
                  className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="sortdir">
                  <option value="desc">Newest first (↓)</option>
                  <option value="asc">Oldest / smallest first (↑)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setStep(3)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50 flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
              <button onClick={runPreview} disabled={loading}
                className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-40 flex items-center gap-1">
                {loading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                {loading ? 'Running…' : 'Preview Report'}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: Preview + Export ── */}
        {step === 5 && preview && (
          <div data-testid="step-preview">
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-base">Preview</h3>
                <p className="text-xs text-slate-500 mt-0.5">{preview.row_count} row{preview.row_count !== 1 ? 's' : ''} matched · showing first 50</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name…"
                  className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs w-44 focus:outline-none focus:border-[#3b4872]"
                  data-testid="template-name-input" />
                <button onClick={saveTemplate} className="px-3 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-display font-bold hover:bg-indigo-50 flex items-center gap-1" data-testid="save-template-btn">
                  <Save size={11} /> Save Template
                </button>
                <button onClick={exportExcel} disabled={loading}
                  className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-bold hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1" data-testid="export-btn">
                  {loading ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Export Excel
                </button>
              </div>
            </div>

            {/* Grouped / aggregated view */}
            {preview.grouped && preview.summary && (
              <table className="w-full dense-table mb-4">
                <thead>
                  <tr>
                    <th>{groupBy?.field || 'Group'}</th>
                    <th className="text-right">{preview.aggregate?.toUpperCase() || 'Count'}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.summary).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td className="text-right tabular-nums">{typeof v === 'number' ? v.toLocaleString('en-IN') : v}</td></tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Flat rows view */}
            {!preview.grouped && (
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {columns.map(c => {
                        const fd = (domains[domain]?.fields || []).find(f => f.field === c);
                        return <th key={c} className="px-3 py-2 text-left font-bold text-slate-700">{fd?.label || c}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(preview.rows || []).map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        {columns.map(c => (
                          <td key={c} className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={String(row[c] ?? '')}>
                            {row[c] !== null && row[c] !== undefined ? String(row[c]) : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {!(preview.rows || []).length && (
                      <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-slate-400">No rows matched the filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button onClick={() => setStep(4)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50 flex items-center gap-1"><ArrowLeft size={12} /> Back</button>
              <button onClick={reset} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50 flex items-center gap-1"><RotateCcw size={12} /> New Report</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
